import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Text, Transformer } from "react-konva";
import { KonvaEventObject } from "konva/lib/Node";
import Konva from "konva";
import { TextItem } from "@exibra/shared";
import { useKonvaSnapping } from "@/hooks/use-konva-snapping";
import { TextFieldCanvasEditor } from "./text-field-canvas-editor";
import { useTranslation } from "react-i18next";

/**
 * This is a component that allows you to edit the text of a text element in the canvas.
 */
interface TextElementEditorProps {
    /** The text item to edit. */
    textItem: TextItem;
    /** The function to call when the text item changes. */
    onChange: (textItem: TextItem) => void;
    /** The function to call when the text item is selected. */
    setSelectedId: (id: string | null) => void;
    /** The id of the selected text item. */
    selectedId: string | null;
}

/**
 * This is a component that allows you to edit the text of a text element in the canvas.
 */
export const TextElementEditor: React.FC<TextElementEditorProps> = ({
    textItem,
    onChange,
    setSelectedId,
    selectedId,
}) => {
    const [t] = useTranslation();
    const transformerRef = useRef<Konva.Transformer>(null);
    const textRef = useRef<Konva.Text>(null);
    const isSelected = useMemo(() => {
        return selectedId === textItem.id;
    }, [selectedId, textItem.id]);
    const [isEditing, setIsEditing] = useState(false);

    const {
        handleDragging: handleSnapping,
        handleResizing: handleSnappingResize,
        handleResizeEnd: handleSnappingResizeEnd,
        handleDragEnd: handleSnappingDragEnd,
    } = useKonvaSnapping({
        snapRange: 10,
        guidelineColor: "red",
        guidelineDash: true,
        showGuidelines: true,
        guidelineThickness: 1,
        snapToStageCenter: true,
        snapToStageBorders: true,
        snapToShapes: true,
    });

    const handleTransform = useCallback(
        (e: KonvaEventObject<MouseEvent>) => {
            handleSnappingResize(e);
            const node = textRef.current;
            if (!node) return;

            const scaleX = node.scaleX();
            const newWidth = node.width() * scaleX;
            onChange({
                ...textItem,
                width: newWidth,
            });

            node.setAttrs({
                width: newWidth,
                scaleX: 1,
            });
        },
        [textItem, onChange, textRef, handleSnappingResize],
    );

    useEffect(() => {
        if (transformerRef.current && textRef.current) {
            transformerRef.current.nodes([textRef.current]);
        }
    }, [isSelected]);

    useEffect(() => {
        if (!isSelected) {
            setIsEditing(false);
        }
    }, [isSelected]);

    useEffect(() => {
        if (!isEditing && textItem.text?.length === 0) {
            onChange({
                ...textItem,
                text: t(`templates.layerTypes.text`),
            });
        }
    }, [isEditing, t, textItem, onChange]);

    return (
        <>
            <Text
                ref={textRef}
                key={textItem.id}
                text={textItem.isPlaceholder ? `[${textItem.text}]` : textItem.text}
                x={textItem.x}
                y={textItem.y}
                offsetX={0}
                offsetY={0}
                width={textItem.width}
                rotation={textItem.rotation}
                draggable={isSelected}
                fontSize={textItem.style.fontSize}
                fontStyle={textItem.style.italic ? "italic" : "normal"}
                fontFamily={textItem.style.font || "Arial"}
                fontVariant={textItem.style.bold ? "bold" : "normal"}
                align={textItem.style.align}
                stroke={selectedId === textItem.id ? "#2196F3" : undefined}
                strokeWidth={selectedId === textItem.id ? 1 : undefined}
                fill={textItem.isPlaceholder ? "#666666" : "#000000"}
                opacity={textItem.isPlaceholder ? 0.8 : 1}
                perfectDrawEnabled={true}
                onDblClick={() => setIsEditing(true)}
                visible={!isEditing}
                onDragMove={handleSnapping}
                onClick={() => {
                    setSelectedId(textItem.id);
                }}
                onTransform={handleTransform}
                onTransformEnd={handleSnappingResizeEnd}
                onDragEnd={(e) => {
                    handleSnappingDragEnd(e);
                    setIsEditing(false);

                    onChange({
                        ...textItem,
                        x: e.target.x(),
                        y: e.target.y(),
                    });
                }}
            />
            {selectedId === textItem.id && !isEditing && (
                <Transformer
                    ref={transformerRef}
                    padding={0}
                    rotateEnabled={true}
                    keepRatio={false}
                    enabledAnchors={["middle-left", "middle-right"]}
                    anchorStroke={isEditing ? "black" : undefined}
                    boundBoxFunc={(oldBox, newBox) => ({
                        ...newBox,
                        width: Math.max(30, newBox.width),
                    })}
                />
            )}

            {isEditing && textRef.current && (
                <TextFieldCanvasEditor
                    onBlur={() => setIsEditing(false)}
                    element={textRef.current}
                    onChange={(text) => {
                        onChange({
                            ...textItem,
                            text,
                        });
                    }}
                />
            )}
        </>
    );
};
